import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import DiscussionThread from './DiscussionThread';
import RoleSignupList from './RoleSignupList';
import { getUserName, isSameUser } from '../utils/userUtils';
import VoteButton from './VoteButton';

export default function BookClub({ users = [] }) {
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  // Book addition states
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [summary, setSummary] = useState("");

  // Book editing states
  const [editingBookId, setEditingBookId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editAuthor, setEditAuthor] = useState("");
  const [editSummary, setEditSummary] = useState("");

  const [commentInputs, setCommentInputs] = useState({});
  const [expandedDiscussionIds, setExpandedDiscussionIds] = useState({});
  const [signingUpId, setSigningUpId] = useState(null);
  const [userNameInput, setUserNameInput] = useState("");

  useEffect(() => {
    setIsLoading(true);
    // Subscribe to Book Club Firestore collection
    const unsubscribe = subscribeToCollection("bookclub", (data) => {
      const sorted = [...data].sort((a, b) => (b.votes || 0) - (a.votes || 0));
      setBooks(sorted);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleVote = async (book) => {
    const userName = localStorage.getItem("prine_user_name") || "";
    if (!userName.trim()) {
      alert("Please enter your name or log in to vote!");
      return;
    }

    const currentVotedBy = Array.isArray(book.votedBy) ? book.votedBy : [];
    let newVotedBy;
    const alreadyVoted = currentVotedBy.some(v => isSameUser(users, v, userName));
    if (alreadyVoted) {
      newVotedBy = currentVotedBy.filter(voter => !isSameUser(users, voter, userName));
    } else {
      newVotedBy = [...currentVotedBy, userName];
    }

    const updated = {
      ...book,
      votedBy: newVotedBy,
      votes: newVotedBy.length
    };

    await saveDocument("bookclub", book.id, updated);
  };

  const handleStartSignup = (bookId) => {
    setSigningUpId(bookId);
    const savedName = localStorage.getItem("prine_user_name") || (users[0]?.id || "");
    setUserNameInput(savedName);
  };

  const handleConfirmSignup = async (book) => {
    if (!userNameInput.trim()) return alert("Please select your name.");

    const currentList = book.readers
      ? book.readers.split(',').map(n => n.trim()).filter(Boolean)
      : [];

    const newName = userNameInput.trim();
    const alreadySignedUp = currentList.some(item => isSameUser(users, item, newName));
    if (!alreadySignedUp) {
      currentList.push(newName);
    }

    const updated = {
      ...book,
      readers: currentList.join(", ")
    };

    await saveDocument("bookclub", book.id, updated);
    setSigningUpId(null);
  };

  const handleLeaveReaders = async (book, nameToLeave) => {
    if (!window.confirm(`Are you sure you want to remove ${getUserName(users, nameToLeave)} from readers?`)) return;
    if (!book.readers) return;
    const currentList = book.readers.split(',').map(n => n.trim());
    const filtered = currentList.filter(n => !isSameUser(users, n, nameToLeave));

    const updated = {
      ...book,
      readers: filtered.join(", ")
    };
    await saveDocument("bookclub", book.id, updated);
  };

  const handleAddComment = async (book) => {
    const text = commentInputs[book.id] || "";
    if (!text.trim()) return;

    const user = localStorage.getItem("prine_user_name") || "Guest";
    const newComment = {
      id: `comm_${Date.now()}`,
      user,
      text: text.trim(),
      time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    };

    const currentComments = book.comments || [];
    const updated = {
      ...book,
      comments: [...currentComments, newComment]
    };

    await saveDocument("bookclub", book.id, updated);
    setCommentInputs(prev => ({ ...prev, [book.id]: "" }));
  };

  const handleDeleteComment = async (book, commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    const currentComments = book.comments || [];
    const updatedComments = currentComments.filter(c => c.id !== commentId);
    const updated = {
      ...book,
      comments: updatedComments
    };
    await saveDocument("bookclub", book.id, updated);
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm("Are you sure you want to delete this book nomination?")) return;
    await deleteDocument("bookclub", bookId);
  };

  const handleStartEdit = (book) => {
    setEditingBookId(book.id);
    setEditTitle(book.title);
    setEditAuthor(book.author);
    setEditSummary(book.summary);
  };

  const handleSaveEdit = async (book) => {
    if (!editTitle.trim() || !editAuthor.trim()) {
      alert("Title and Author are required.");
      return;
    }
    const updated = {
      ...book,
      title: editTitle.trim(),
      author: editAuthor.trim(),
      summary: editSummary.trim()
    };
    await saveDocument("bookclub", book.id, updated);
    setEditingBookId(null);
  };

  const handleAddNomination = async (e) => {
    e.preventDefault();
    if (!title || !author) return;

    const nominator = localStorage.getItem("prine_user_name") || "Guest";
    const newBook = {
      id: `book_${Date.now()}`,
      title: title.trim(),
      author: author.trim(),
      summary: summary.trim(),
      nominatedBy: nominator,
      votes: 0,
      votedBy: [],
      readers: nominator,
      comments: []
    };

    await saveDocument("bookclub", newBook.id, newBook);
    setShowAddForm(false);
    // Reset Form
    setTitle("");
    setAuthor("");
    setSummary("");
  };

  return (
    <div className="tab-panel">
      <div className="view-title-container">
        <div className="view-title">
          <h2>📖 Family Book Club</h2>
        </div>
        <button className="btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? "Cancel Nomination" : "+ Nominate Book"}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddNomination} className="glass-panel" style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', color: 'var(--color-text-main)' }}>📚 Nominate a Summer Read</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Book Title</label>
              <input type="text" placeholder="e.g. The Hobbit" className="input-field" value={title} onChange={e => setTitle(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Author</label>
              <input type="text" placeholder="e.g. J.R.R. Tolkien" className="input-field" value={author} onChange={e => setAuthor(e.target.value)} required />
            </div>
          </div>
          <div className="form-group">
            <label>Summary / Pitch</label>
            <textarea placeholder="Describe why this book is perfect for the pool lounge or cozy fireside reading." className="input-field" value={summary} onChange={e => setSummary(e.target.value)} rows="3" required />
          </div>
          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>Submit Nomination</button>
        </form>
      )}

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-chip">
            <span className="spinner"></span>
            <span>Syncing Book Club Board...</span>
          </div>
        </div>
      ) : books.length === 0 ? (
        <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          No book nominations yet. Start recommending books above!
        </div>
      ) : (
        <div className="book-grid">
          {books.map(book => {
            const userName = localStorage.getItem("prine_user_name") || "";
            const currentVotedBy = Array.isArray(book.votedBy) ? book.votedBy : [];
            const hasVoted = userName ? currentVotedBy.includes(userName) : false;
            const readersList = book.readers
              ? book.readers.split(',').map(n => n.trim()).filter(Boolean)
              : [];

            const isEditing = editingBookId === book.id;

            if (isEditing) {
              return (
                <div key={book.id} className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '24px', height: '100%' }}>
                  <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', color: 'var(--color-text-main)', margin: '0 0 8px 0' }}>Edit Book Nomination</h3>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600' }}>Book Title</label>
                    <input
                      type="text"
                      className="input-field"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      placeholder="Book Title"
                      style={{ fontSize: '0.9rem', fontFamily: 'var(--font-body)' }}
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600' }}>Author</label>
                    <input
                      type="text"
                      className="input-field"
                      value={editAuthor}
                      onChange={e => setEditAuthor(e.target.value)}
                      placeholder="Author"
                      style={{ fontSize: '0.9rem', fontFamily: 'var(--font-body)' }}
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600' }}>Summary / Pitch</label>
                    <textarea
                      className="input-field"
                      value={editSummary}
                      onChange={e => setEditSummary(e.target.value)}
                      placeholder="Summary..."
                      rows="3"
                      style={{ fontSize: '0.9rem', fontFamily: 'var(--font-body)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      className="btn-primary"
                      onClick={() => handleSaveEdit(book)}
                      style={{ fontSize: '0.8rem', padding: '6px 16px', fontFamily: 'var(--font-body)' }}
                    >
                      Save
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => setEditingBookId(null)}
                      style={{ fontSize: '0.8rem', padding: '6px 16px', fontFamily: 'var(--font-body)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            }

            const isBookExpanded = expandedDiscussionIds[book.id] !== undefined
              ? expandedDiscussionIds[book.id]
              : (book.comments || []).length > 0;

            return (
              <div key={book.id} className="glass-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.3rem', color: 'var(--color-text-main)' }}>{book.title}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', fontWeight: '600' }}>by {book.author}</span>
                      {book.nominatedBy && (
                        <span style={{ color: 'var(--color-secondary)', background: 'rgba(245, 158, 11, 0.08)', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', fontWeight: '600', fontFamily: 'var(--font-body)' }}>
                          Suggested by: {getUserName(users, book.nominatedBy)}
                        </span>
                      )}
                      {book.nominatedBy && userName && isSameUser(users, book.nominatedBy, userName) && (
                        <div style={{ display: 'inline-flex', gap: '6px' }}>
                          <button
                            onClick={() => handleStartEdit(book)}
                            className="btn-inline-edit"
                            title="Edit book nomination"
                          >
                            [Edit]
                          </button>
                          <button
                            onClick={() => handleDeleteBook(book.id)}
                            className="btn-inline-delete"
                            title="Delete nomination"
                          >
                            [Delete]
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <VoteButton
                      votes={book.votes || 0}
                      hasVoted={hasVoted}
                      onVote={() => handleVote(book)}
                      votedBy={currentVotedBy}
                      users={users}
                      itemType="book"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDiscussionIds(prev => ({
                          ...prev,
                          [book.id]: !isBookExpanded
                        }));
                      }}
                      style={{
                        background: isBookExpanded ? 'rgba(2, 132, 199, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                        border: isBookExpanded ? '1px solid rgba(2, 132, 199, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '20px',
                        padding: '4px 10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color: isBookExpanded ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        fontFamily: 'var(--font-body)'
                      }}
                      title="Reply / Discussion"
                    >
                      💬 {(book.comments || []).length}
                    </button>
                  </div>
                </div>

                {currentVotedBy.length > 0 && (
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-text-muted)',
                    marginBottom: '12px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    alignSelf: 'flex-start',
                    border: '1px solid rgba(255, 255, 255, 0.03)'
                  }}>
                    <span>❤️ Liked by:</span>
                    <span style={{ color: 'var(--color-text-main)', fontWeight: '500' }}>
                      {currentVotedBy.map(v => getUserName(users, v)).join(', ')}
                    </span>
                  </div>
                )}

                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: '1.5', marginBottom: '16px', flex: 1 }}>{book.summary}</p>

                {/* Readers list */}
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <RoleSignupList
                    label="📖 Readers"
                    names={readersList}
                    isSigningUp={signingUpId === book.id}
                    userNameInput={userNameInput}
                    onUserNameChange={setUserNameInput}
                    onStartSignup={() => handleStartSignup(book.id)}
                    onConfirmSignup={() => handleConfirmSignup(book)}
                    onCancelSignup={() => setSigningUpId(null)}
                    onLeave={(name) => handleLeaveReaders(book, name)}
                    users={users}
                    buttonLabel="+ Join Readers"
                    borderColor="rgba(2, 132, 199, 0.18)"
                    accentColor="var(--color-primary)"
                  />
                </div>

                {/* Discussions / Comments */}
                <DiscussionThread
                  comments={book.comments}
                  commentInput={commentInputs[book.id] || ""}
                  onCommentInputChange={(val) => setCommentInputs(prev => ({ ...prev, [book.id]: val }))}
                  onAddComment={() => handleAddComment(book)}
                  onDeleteComment={(commentId) => handleDeleteComment(book, commentId)}
                  placeholder="Comment on this read..."
                  users={users}
                  isExpanded={isBookExpanded}
                />
              </div>
            );
          })}
        </div>
      )
      }
    </div >
  );
}
